import React, { useId, useMemo } from 'react';
import { InitialAvatar } from '../InitialAvatar';
import { MEMBERS, TIMELINE_END, TIMELINE_START, memberSpans } from '../../data/community';
import { itemsInYears } from '../../data/archive';
interface PeopleTimelineProps {
  start: number;
  end: number;
  onChange: (span: {
    start: number;
    end: number;
  }) => void;
}
const DECADE_TICKS = [1976, 1986, 1996, 2006, 2016, TIMELINE_END];

/**
 * The organising idea, made about people rather than about data.
 *
 * Dragging the years repopulates a row of faces, the same mechanic as the
 * member directory, but warm and small, so it reads as meeting people rather
 * than operating software. Portraits are prototype placeholders; publishing a
 * real member's face and first name needs their explicit consent.
 */
export function PeopleTimeline({
  start,
  end,
  onChange
}: PeopleTimelineProps) {
  const id = useId();
  const span = TIMELINE_END - TIMELINE_START;
  const present = useMemo(() => MEMBERS.filter(member => memberSpans(member).some(s => s.start <= end && s.end >= start)), [start, end]);
  const shown = present.slice(0, 5);
  const items = useMemo(() => itemsInYears(start, end).length, [start, end]);
  const left = (start - TIMELINE_START) / span * 100;
  const width = (end - start) / span * 100;
  return <div role="group" aria-labelledby={`${id}-title`} className="flex flex-col gap-5 rounded-card border border-rule bg-card p-5 shadow-card sm:p-6" data-id="element-172" data-mp="11i6m6tydwtqz">
      <div className="flex flex-col gap-1" data-id="element-173" data-mp="1t7rlj3176c9y">
        <h2 id={`${id}-title`} className="text-heading font-semibold text-ink" data-id="element-174" data-mp="1tax92du6h63k">
          Who else was here when you were?
        </h2>
        <p className="text-small text-ink-muted" data-id="element-175" data-mp="1kfze717gzj9l">
          Slide to your years. You don’t need an account to try it.
        </p>
      </div>

      {/* Faces for the current span. */}
      <ul className="flex min-h-[7.5rem] flex-wrap items-end gap-2.5" data-id="element-176" data-mp="2cwtqauods1zi">
        {shown.length > 0 ? shown.map((member, index) => <li key={member.name} className="flex w-[4.5rem] flex-col gap-1.5" style={{
        marginBottom: index % 2 === 1 ? '0.75rem' : 0
      }} data-id="element-177" data-mp="2mrhfvh8xt0j1" data-mpi={index}>
              {member.photo ? <img src={member.photo} alt="" className="aspect-square w-full rounded-field object-cover" data-id="element-178" data-mp="2cwpf64k25xk1" /> : <span className="flex aspect-square w-full items-center justify-center rounded-field bg-canvas" data-id="element-179" data-mp="23w11xc2ho187">
                  <InitialAvatar name={member.name} size={40} data-id="element-180" data-mp="1u44pevzno8kg" />
                </span>}
              <span className="truncate text-small font-medium text-ink" data-id="element-181" data-mp="1p51wub0v3utd">
                {member.name.split(' ')[0]}
              </span>
              <span className="font-mono text-eyebrow-caption tracking-[0.08em] text-ink-muted" data-id="element-182" data-mp="1k4vy6aw6d81s">
                {member.years.replace('–present', '–now')}
              </span>
            </li>) : <li className="text-small text-ink-muted" data-id="element-183" data-mp="3rr5tauf3wq18">
            Nobody from these years has joined yet. You’d be the first.
          </li>}
      </ul>

      <p aria-live="polite" className="text-body text-ink" data-id="element-184" data-mp="4mhmwsmhztkx">
        <span className="font-semibold" data-id="element-185" data-mp="4kwpmv8stwjd">
          {present.length} {present.length === 1 ? 'person' : 'people'} here
        </span>{' '}
        <span className="text-ink-muted" data-id="element-186" data-mp="38484wk70drsu">
          {present.length === 1 ? 'overlaps' : 'overlap'} with {start}–{end}.
          {items > 0 ? ` There ${items === 1 ? 'is 1 thing' : `are ${items} things`} in the archive from those years.` : ''}
        </span>
      </p>

      {/* The axis. Decorative; the range inputs carry the semantics. */}
      <div aria-hidden="true" className="flex flex-col gap-2 border-t border-rule pt-4" data-id="element-187" data-mp="3mufgcfylmak1">
        <div className="relative h-6" data-id="element-188" data-mp="2pb484pyhuyu0">
          <div className="absolute inset-x-0 top-2.5 h-1.5 rounded-pill bg-canvas" data-id="element-189" data-mp="2ubrwt4z933l2" />
          <div className="absolute top-2.5 h-1.5 rounded-pill bg-primary" style={{
          left: `${left}%`,
          width: `${Math.max(width, 1)}%`
        }} data-id="element-190" data-mp="zvtl8t3mgi4d" />
          <div className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-pill bg-ink px-2 py-0.5 font-mono text-eyebrow-caption tracking-[0.08em] text-on-ink" style={{
          left: `${Math.min(Math.max(left + width / 2, 10), 90)}%`
        }} data-id="element-191" data-mp="vset542esbak">
            {start}–{end}
          </div>
        </div>
        <ol className="flex justify-between font-mono text-eyebrow-caption tracking-[0.08em] text-ink-subtle" data-id="element-192" data-mp="1el97bvs7jzdq">
          {DECADE_TICKS.map(year => <li key={year} data-id="element-193" data-mp="1sjz6dgyl894q">{year}</li>)}
        </ol>
      </div>

      <div className="grid gap-3 sm:grid-cols-2" data-id="element-194" data-mp="1nmqo7gdfw53h">
        <label className="flex flex-col gap-1" data-id="element-195" data-mp="1njupvqxojeei">
          <span className="text-small font-medium text-ink" data-id="element-196" data-mp="3ud6ikhiwmame">
            From <span className="text-ink-muted" data-id="element-197" data-mp="3pdag4mpuur8y">{start}</span>
          </span>
          <input type="range" min={TIMELINE_START} max={TIMELINE_END} value={start} aria-label="First year you were around" onChange={event => {
          const next = Number(event.target.value);
          onChange({
            start: next,
            end: Math.max(end, next)
          });
        }} className="h-tap w-full accent-[color:var(--color-primary)]" data-id="element-198" data-mp="3pfeslt1tkyku" />
        </label>

        <label className="flex flex-col gap-1" data-id="element-199" data-mp="1m3gssjylnh3n">
          <span className="text-small font-medium text-ink" data-id="element-200" data-mp="1m6ux3xqtq0dq">
            To <span className="text-ink-muted" data-id="element-201" data-mp="1r568icwroiy2">{end}</span>
          </span>
          <input type="range" min={TIMELINE_START} max={TIMELINE_END} value={end} aria-label="Most recent year you were around" onChange={event => {
          const next = Number(event.target.value);
          onChange({
            start: Math.min(start, next),
            end: next
          });
        }} className="h-tap w-full accent-[color:var(--color-primary)]" data-id="element-202" data-mp="1d4lqhiyoer5m" />
        </label>
      </div>
    </div>;
}